import * as React from 'react';

import { DEFAULT_VOICEBOX_PANEL_DATA } from 'bones/components/molecules/Voicebox/context/panelData/data';
import { RDFS_SUBCLASS_IRI } from 'bones/constants/iris';
import {
  RelationshipDirection,
  VoiceboxPanelData,
  VoiceboxPanelDataContextValue,
} from 'bones/constants/voicebox/panel';
import { VoiceboxSettingsContextValue } from 'bones/constants/voicebox/settings';
import { useIsMounted } from 'bones/utils/hooks/useIsMounted';
import { asyncNoop } from 'bones/utils/noop';
import { QueryBuilderModel } from 'bones/utils/queryBuilder/model';
import { usePortalVoiceboxAttributesFetcher } from 'bones/utils/voicebox/usePortalVoiceboxAttributesFetcher';
import { usePortalVoiceboxInstancesFetcher } from 'bones/utils/voicebox/usePortalVoiceboxInstancesFetcher';
import { usePortalVoiceboxLabelsFetcher } from 'bones/utils/voicebox/usePortalVoiceboxLabelsFetcher';
import { usePortalVoiceboxProvenanceFetcher } from 'bones/utils/voicebox/usePortalVoiceboxProvenanceFetcher';
import { usePortalVoiceboxRelationshipsFetcher } from 'bones/utils/voicebox/usePortalVoiceboxRelationshipsFetcher';
import { usePortalVoiceboxTypesFetcher } from 'bones/utils/voicebox/usePortalVoiceboxTypesFetcher';

export const useFetchDataForIri = ({
  catalogDatabaseId,
  connectionIdx,
  labels,
  limit,
  model,
  provenance,
  setDataByIri,
  setLabels,
  setProvenance,
  settingsContext,
}: {
  catalogDatabaseId: string | undefined;
  connectionIdx: number;
  labels: VoiceboxPanelDataContextValue['labels'];
  limit: number;
  model: QueryBuilderModel | undefined;
  provenance: VoiceboxPanelDataContextValue['provenance'];
  setDataByIri: React.Dispatch<
    React.SetStateAction<VoiceboxPanelDataContextValue['dataByIri']>
  >;
  setLabels: React.Dispatch<
    React.SetStateAction<VoiceboxPanelDataContextValue['labels']>
  >;
  setProvenance: React.Dispatch<
    React.SetStateAction<VoiceboxPanelDataContextValue['provenance']>
  >;
  settingsContext: VoiceboxSettingsContextValue;
}) => {
  const isMounted = useIsMounted();

  const makeSetterForIri = React.useCallback(
    <Key extends keyof VoiceboxPanelData>(key: Key) =>
      (iri: string, action: React.SetStateAction<VoiceboxPanelData[Key]>) => {
        // istanbul ignore if
        if (!isMounted.current) {
          return;
        }

        setDataByIri((prevByIri) => {
          const prevData = prevByIri[iri] || DEFAULT_VOICEBOX_PANEL_DATA;
          return {
            ...prevByIri,
            [iri]: {
              ...prevData,
              [key]: {
                ...prevData[key],
                ...(typeof action === 'function'
                  ? action(prevData[key])
                  : /* istanbul ignore next */ action),
              },
            },
          };
        });
      },
    [isMounted, setDataByIri]
  );

  const setLoadedForIri = React.useMemo(
    () => makeSetterForIri('loaded'),
    [makeSetterForIri]
  );

  const setInstancesForIri = React.useMemo(
    () => makeSetterForIri('instances'),
    [makeSetterForIri]
  );

  const setTypesForIri = React.useMemo(
    () => makeSetterForIri('types'),
    [makeSetterForIri]
  );

  const setAttributesForIri = React.useMemo(
    () => makeSetterForIri('attributes'),
    [makeSetterForIri]
  );

  const setRelationshipsForIri = React.useMemo(
    () => makeSetterForIri('relationships'),
    [makeSetterForIri]
  );

  const fetchLabels = usePortalVoiceboxLabelsFetcher(
    connectionIdx,
    settingsContext,
    labels.labelsByIri,
    setLabels
  );

  const fetchInstances = usePortalVoiceboxInstancesFetcher(
    connectionIdx,
    limit,
    settingsContext,
    fetchLabels,
    setInstancesForIri
  );

  const fetchTypes = usePortalVoiceboxTypesFetcher(
    connectionIdx,
    settingsContext,
    setTypesForIri
  );

  const fetchAttributes = usePortalVoiceboxAttributesFetcher(
    connectionIdx,
    limit,
    settingsContext,
    fetchLabels,
    setAttributesForIri
  );

  const fetchRelationships = usePortalVoiceboxRelationshipsFetcher(
    connectionIdx,
    limit,
    settingsContext,
    fetchLabels,
    setRelationshipsForIri
  );

  const fetchProvenance = usePortalVoiceboxProvenanceFetcher(
    connectionIdx,
    catalogDatabaseId,
    settingsContext,
    provenance,
    setProvenance
  );

  return React.useCallback(
    async (iri: string) => {
      // initialize empty data
      setLoadedForIri(iri, (prev) => prev);

      // fetch iri label first
      await fetchLabels([iri]);
      setLoadedForIri(iri, (prev) => ({ ...prev, label: true }));

      const [typeIris] = await Promise.all([
        fetchTypes(iri),
        fetchInstances(iri, 0),
      ]);

      // fetch attributes and relationships second since they require types
      await Promise.all([
        fetchAttributes(iri, typeIris || []),
        fetchRelationships(iri, typeIris || []),
      ]);

      const modelConceptClass = model?.classesByIri[iri];

      // fetch provenance for concepts
      await fetchProvenance(
        modelConceptClass ? [iri] : typeIris || /* istanbul ignore next */ []
      );

      if (modelConceptClass) {
        setRelationshipsForIri(iri, (prev) => {
          const relationships = [...prev.relationships];

          // add parent classes
          if (modelConceptClass.ancestors.size) {
            relationships.push({
              direction: RelationshipDirection.OUTGOING,
              predicateIri: RDFS_SUBCLASS_IRI,
              items: [...modelConceptClass.ancestors],
              count: modelConceptClass.ancestors.size,
              loading: false,
              hasMore: false,
              loadingMore: false,
              loadMore: asyncNoop,
            });
          }

          // add child classes
          if (modelConceptClass.descendants.size) {
            relationships.push({
              direction: RelationshipDirection.INCOMING,
              predicateIri: RDFS_SUBCLASS_IRI,
              items: [...modelConceptClass.descendants],
              count: modelConceptClass.descendants.size,
              loading: false,
              hasMore: false,
              loadingMore: false,
              loadMore: asyncNoop,
            });
          }

          // add model concept relationships
          Object.values(model.propertiesByIri).forEach((property) => {
            if (property.isDatatype) {
              return;
            }

            if (property.domains.has(iri)) {
              relationships.push({
                direction: RelationshipDirection.OUTGOING,
                predicateIri: property.iri,
                items: [...property.ranges],
                count: property.ranges.size,
                loading: false,
                hasMore: false,
                loadingMore: false,
                loadMore: asyncNoop,
              });
            }

            if (property.ranges.has(iri)) {
              relationships.push({
                direction: RelationshipDirection.INCOMING,
                predicateIri: property.iri,
                items: [...property.domains],
                count: property.domains.size,
                loading: false,
                hasMore: false,
                loadingMore: false,
                loadMore: asyncNoop,
              });
            }
          });
          return { ...prev, relationships };
        });
      }

      setLoadedForIri(iri, (prev) => ({ ...prev, body: true }));
    },
    [
      fetchAttributes,
      fetchInstances,
      fetchLabels,
      fetchProvenance,
      fetchRelationships,
      fetchTypes,
      model,
      setLoadedForIri,
      setRelationshipsForIri,
    ]
  );
};
